<script lang="ts">
import type {PropType} from "vue";
import type {CSSProperties} from "vue";
import type {AxiosResponse} from "axios";
import type {SelectionValue, SelectionRow, SelectionOption, SelectionModel, SelectionMapper, SelectionTransform} from "@/types/controls";


import {defineComponent} from "vue";
import axios from "axios";
import Input from "@/components/Input.vue";
import {LoadingType} from "@/type/LoadingType";
import LoadingIcon from "@/assets/icons/LoadingIcon.vue";
import {errorUtil} from "@/utils/ErrorUtil";
import ArrowControl from "@/assets/icons/ArrowControlIcon.vue";
import Checkbox from "@/components/Checkbox.vue";
import Debug from "@/components/utils/Debug.vue";
import {loadTranslationObjectFiled} from "@/utils/ApiUti";
import {TranslationType} from "@/type/TranslationType";

export default defineComponent({
  name: "Multiselect",
  components: {Debug, Checkbox, ArrowControl, LoadingIcon, Input},
  props: {
    modelValue: {
      type: [Array, String, Number, Boolean] as PropType<SelectionModel>,
      default: () => [],
    },

    items: {
      type: Array as PropType<unknown[]>,
      default: () => [],
    },

    listUrl: {
      type: String,
      required: false,
    },

    listUrlParams: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    itemUrl: {
      type: String,
      required: false,
    },

    itemUrlParams: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    label: {
      type: String,
      default: "&nbsp;",
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: [String, Number],
      default: "Select an option",
    },

    customClass: {
      type: String,
    },

    errorMessage: {
      type: String,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    maxSize: {
      type: Number,
      default: 1,
    },

    itemMapper: {
      type: Function as PropType<SelectionMapper>,
    },

    dataMapper: {
      type: Function as PropType<SelectionMapper>,
    },

    transformData: {
      type: Function as PropType<SelectionTransform>,
    },

    disabledSearch: {
      type: Boolean,
      default: false,
    },

    filterItemsFun: {
      type: Function as PropType<{filter(items: SelectionRow[], search: string): SelectionRow[]}["filter"]>,
    },

    fieldTranslationExtractorIdFun: {
      type: Function as PropType<(item: SelectionRow) => string | undefined>,
    },

    loadItemDetails: {
      type: Boolean,
      default: false,
    },

    dropdownPlacement: {
      type: String as PropType<'auto' | 'top'>,
      default: 'auto'
    },

    // Nested filter menus stay inside their parent popup for outside-click handling.
    inlineDropdown: {
      type: Boolean,
      default: false
    },

    sortData: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      isOpen: false,
      isVisible: false,
      selectedItems: [] as SelectionRow[],

      searchText: '',
      pos: 0,
      amount: 50,
      observer: null as IntersectionObserver | null,

      data: [] as SelectionRow[],

      loading: false,
      loadedAll: false,
      noDataLoaded: false,
      isDataLoaded: false,

      dropdownPosition: 'bottom', // 'top' or 'bottom'
      teleportStyle: {
        position: 'absolute',
        top: '0px',
        left: '0px',
        width: 'auto',
        zIndex: 9999999,
      } as CSSProperties,
    };
  },

  computed: {
    LoadingType() {
      return LoadingType
    },

    selectedItemText() {
      return this.multiple
          ? this.selectedItems.map(item => item.name).join(', ')
          : this.selectedItems[0].name;
    },

    filteredItems() {
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });

      if (this.listUrl) return this.data;

      const search = this.searchText.toLowerCase();

      if (this.filterItemsFun) {
        return this.filterItemsFun(this.data, search);
      }

      return this.data.filter(item => item.name!.replace(/\s+/g, '').toLowerCase().includes(search));
    },

    dataDropdown() {
      return this.isOpen ? 'open' : 'default';
    },
  },

  watch: {
    searchText: {
      async handler() {
        if (this.itemUrl || this.listUrl) {
          await this.resetAndFetchItems(true);
        }
      },
      deep: true
    },

    items: {
      handler(newValue: unknown[]) {
        const formatFunction = this.dataMapper || ((item: unknown) => ({id: (item as SelectionOption).value, name: (item as SelectionOption).text}));
        const formattedData = newValue.map(formatFunction);

        if (this.sortData) {
          formattedData.sort((a, b) => a.name!.localeCompare(b.name!));
        }

        this.data = [...formattedData];

        if (!this.listUrl && !this.itemUrl) this.noDataLoaded = this.data.length === 0;
      },
      deep: true,
      immediate: true
    },

    modelValue: {
      immediate: true,
      handler(newValue: SelectionModel) {
        if (this.isDataLoaded) return

        if (this.multiple || Array.isArray(newValue)) {
          this.selectedItems = this.data.filter(item => (newValue as SelectionValue[]).includes(item.id));
        } else {
          const selectedItem = this.data.find(item => item.id === newValue);
          this.selectedItems = selectedItem ? [selectedItem] : [];
        }
      },
    },

    listUrlParams: {
      handler(oldValue, newValue) {
        if (!oldValue) return
        if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return;

        this.resetAndFetchItems()
      },
      deep: true
    },

    data: {
      handler() {
        if (this.dropdownPlacement === 'top' && this.isOpen) {
          this.$nextTick(() => this.updateDropdownPosition());
        }
      },
      deep: true
    },

    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    }
  },

  methods: {
    async loadData(infinitySearch = false) {
      if (this.loading || this.loadedAll) return;
      this.isDataLoaded = true;

      this.loading = true;

      const {data} = (await axios.get<unknown>(this.listUrl!, {
        params: {
          ...this.listUrlParams,
          pos: this.pos,
          amount: this.amount,
          ...(this.searchText && {search: this.searchText}),
        },
      }).catch(async (error) => {
        this.loading = false;
        this.noDataLoaded = true;
        await errorUtil.catchErrors(error);
      })) as AxiosResponse<unknown>;

      const transformedData = (this.transformData ? this.transformData(data) : data) as SelectionRow[];

      if (!transformedData?.length) {
        if (!this.data.length) this.noDataLoaded = true;

        this.loadedAll = true;
        this.loading = false;
        return;
      }

      if (transformedData.length < this.amount) {
        this.loadedAll = true;
      }

      const selectedItemsMap = new Map(this.selectedItems.map(item => [item.id, item.name]));

      this.data = this.data.map(item =>
          !item.name && selectedItemsMap.has(item.id)
              ? {...item, name: selectedItemsMap.get(item.id)!}
              : item
      );

      const existingIds = new Set(this.data.map(item => item.id));
      const uniqueData = transformedData.filter(item => !existingIds.has(item.id));

      const formattedData = uniqueData.map(this.dataMapper || (item => ({id: item.id, name: item.name})));

      this.data = [...this.data, ...formattedData];

      if (infinitySearch) {
        this.selectedItems = this.data.filter(item => (this.modelValue as SelectionValue[]).includes(item.id));

        if (this.selectedItems.length === 0) {
          await this.loadData(true);
        }
      }

      this.loading = false;

      this.observeSentinel();
    },

    clearSelectedItems() {
      this.selectedItems = [];
    },

    async resetAndFetchItems(saveSelectedItems = false) {
      this.pos = 0;
      this.data = [];

      if (!saveSelectedItems) {
        this.selectedItems = [];
        this.$emit("update:modelValue", this.multiple ? [] : '');
        this.$emit('extra-data', this.selectedItems)
      }

      this.loadedAll = false;
      this.noDataLoaded = false;
      await this.loadData();
    },

    async loadMoreItems() {
      if (this.loading || this.loadedAll) return

      this.pos += this.amount;
      await this.loadData();
    },

    observeSentinel() {
      const sentinel = (this.$refs.sentinel as HTMLElement);
      if (sentinel) {
        if (this.observer) {
          this.observer.disconnect();
        }
        this.observer!.observe(sentinel);
      }
    },

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver(this.handleIntersection, options);
    },

    handleIntersection(entries: IntersectionObserverEntry[]) {
      const entry = entries[0];
      if (entry.isIntersecting && !this.loading && !this.loadedAll && !this.noDataLoaded) {
        this.loadMoreItems();
      }
    },

    async handleToggleDropdown() {
      if (this.disabled) return;

      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        await this.openDropdown();

        requestAnimationFrame(() => {
          this.updateDropdownPosition();
        });

        this.isVisible = true;

        await this.$nextTick(() => {
          if (!this.disabledSearch && (this.$refs.searchInput as InstanceType<typeof Input>)) {
            ;(this.$refs.searchInput as InstanceType<typeof Input>).focus();
          }
        });
      } else {
        this.closeDropdown();
      }
    },

    async openDropdown() {
      await this.$nextTick();
      this.setDropdownWidth();

      if (this.listUrl) {
        this.initialDataLoad();
      }
    },

    closeDropdown() {
      if (this.observer) this.observer.disconnect();
    },

    setDropdownWidth() {
      const optionWidth = (this.$refs.dropdownOption as HTMLElement).offsetWidth;
      ;(this.$refs.dropdownList as HTMLElement).style.width = `${optionWidth}px`;
    },

    initialDataLoad() {
      if (this.data.length <= 1) {
        this.loadData();
      } else {
        this.observeSentinel();
        this.loadData();
      }
    },

    isSelected(id: SelectionValue) {
      return this.selectedItems.some(item => item.id === id);
    },

    async selectItem(id: SelectionValue, event?: MouseEvent) {
      // The nested menu disappears on selection before the parent handles the document click.
      if (this.inlineDropdown) {
        event?.stopPropagation();
      }


      const selectedItem = this.data.find(item => item.id === id)!;

      if (this.multiple) {
        const index = this.selectedItems.findIndex(item => item.id === id);

        if (index > -1) {
          this.selectedItems.splice(index, 1);
        } else if (this.maxSize === 0 || this.selectedItems.length < this.maxSize) {
          this.selectedItems.push(selectedItem);
        } else if (this.maxSize === 1) {
          this.selectedItems = [selectedItem];
        }

        this.$emit("update:modelValue", this.selectedItems.map(item => item.id));
        this.$emit('extra-data', this.selectedItems);
      } else {
        if (this.selectedItems.length && this.selectedItems[0].id === id) {
          this.selectedItems = [];
          this.$emit("update:modelValue", '');
          this.$emit('extra-data', [])
        } else {
          if (this.itemUrl && this.loadItemDetails) {
            await this.loadItem(selectedItem.id)
          }

          this.selectedItems = [selectedItem];
          this.$emit("update:modelValue", selectedItem.id);
          this.$emit('extra-data', [selectedItem])
        }

        this.isOpen = false;
      }
    },

    handleClickOutside(event: MouseEvent) {
      if (!this.isOpen || this.disabled || !this.isVisible) return;

      const dropdown = (this.$refs.dropdown as HTMLElement);
      const searchInput = (this.$refs.searchInput as InstanceType<typeof Input>)?.$el || (this.$refs.searchInput as InstanceType<typeof Input>);
      const dropdownList = (this.$refs.dropdownList as HTMLElement);

      if (
          !dropdown.contains(event.target as Node) &&
          !searchInput?.contains(event.target as Node) &&
          !dropdownList.contains(event.target as Node)
      ) {
        this.isOpen = false;
      }
    },

    handleEnter() {
      if (this.filteredItems.length === 1) {
        this.selectItem(this.filteredItems[0].id);

        if (!this.listUrl) this.searchText = '';
      }
    },

    handleKeydown(event: { key: string; }) {
      if (event.key === 'Escape') {
        this.isOpen = false;
      }
    },

    isEmpty(value: unknown) {
      return (
          value == null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'object' && Object.keys(value).length === 0)
      );
    },

    async loadItem(this: import("@/types/controls").SelectionLoaderContext, value: SelectionModel = this.modelValue) {
      if (this.isEmpty(value)) {
        this.loading = false;
        return;
      }

      this.loading = true;
      let results: unknown[] = [];

      const values = Array.isArray(value) ? value : [value];

      for (const v of values) {
        try {
          let {data} = await axios.get<unknown>(`${this.itemUrl}/${v}`, {
            params: {
              ...this.itemUrlParams
            },
          });
          if (data) results.push(...(Array.isArray(data) ? data : [data]));
        } catch (error) {
          await errorUtil.catchErrors(error);
        }
      }

      if (!results.length) {
        this.noDataLoaded = true;
        this.loading = false;
        return;
      }

      let formattedData = this.itemMapper ? results.map(this.itemMapper) : results as SelectionRow[]

      this.mergeItemsById(this.data, formattedData);

      const selectedItemsMap = new Map(this.selectedItems.map(item => [item.id, item]));

      formattedData.forEach(item => {
        const isSelected = Array.isArray(this.modelValue)
            ? this.modelValue.includes(item.id)
            : item.id === this.modelValue;

        if (isSelected) {
          if (selectedItemsMap.has(item.id)) {
            Object.assign(selectedItemsMap.get(item.id)!, item);
          } else {
            selectedItemsMap.set(item.id, item);
          }
        }
      });

      const selectedItems = Array.from(selectedItemsMap.values());

      if (this.fieldTranslationExtractorIdFun) {
        const enrichedItems = [];
        for (const item of selectedItems) {
          const fieldId = this.fieldTranslationExtractorIdFun(item);
          const enriched = await this.enrichWithTranslation([item], fieldId!);
          enrichedItems.push(...enriched);
        }
        this.selectedItems = enrichedItems;
      } else {
        this.selectedItems = selectedItems;
        this.data = selectedItems;
      }

      this.$emit('extra-data', this.filteredItems);

      this.loading = false;
    },

    async enrichWithTranslation(items: SelectionRow[], fieldId: string) {
      return await Promise.all(items.map(async item => {
        if (item.name) return item;

        const translatedData = await loadTranslationObjectFiled(TranslationType.OTHER, item.id as string, fieldId);
        const enTranslationText = translatedData.find((t: { language: string; }) => t.language === 'en')?.text || '';

        return {id: item.id, name: enTranslationText};
      }));
    },

    mergeItemsById(baseItems: SelectionRow[], newItems: SelectionRow[]) {
      const baseMap = new Map(baseItems.map(item => [item.id, item]));

      newItems.forEach(item => {
        const existing = baseMap.get(item.id);
        if (existing) {
          Object.assign(existing, item);
        } else {
          baseItems.push(item);
        }
      });
    },

    updateDropdownPosition() {
      if (!this.isOpen) return;

      const option = (this.$refs.dropdownOption as HTMLElement);
      const list = (this.$refs.dropdownList as HTMLElement);

      if (!option || !list) return;

      const optionRect = option.getBoundingClientRect();

      const forceAbove = this.dropdownPlacement === 'top';
      const maxHeight = this.$slots['dropdown-header'] ? 400 : 250;
      const dropdownHeight = forceAbove
          ? Math.min(list.scrollHeight, maxHeight)
          : list.offsetHeight;
      const spaceBelow = window.innerHeight - optionRect.bottom;
      const spaceAbove = optionRect.top;

      const fitsBetterAbove = spaceBelow < dropdownHeight + 100 && spaceAbove > dropdownHeight;
      const renderAbove = forceAbove || fitsBetterAbove;
      const popupTop = renderAbove ? optionRect.top - dropdownHeight : optionRect.bottom;
      const top = forceAbove ? Math.max(8, popupTop) : popupTop;

      const left = optionRect.left;
      const width = optionRect.width;

      this.dropdownPosition = renderAbove ? 'top' : 'bottom';
      this.teleportStyle = {
        position: this.inlineDropdown || forceAbove ? 'fixed' : 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
        zIndex: 999999,
      };
    },
  },

  async created() {
    if (this.items.length > 0) return

    if (this.data.length === 0 && this.itemUrl && this.modelValue) {
      await this.loadItem()
      return
    }

    if (this.listUrl) {
      await this.loadData(true)
    }
  },

  mounted() {
    this.setupIntersectionObserver();

    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.updateDropdownPosition);
    window.addEventListener('scroll', this.updateDropdownPosition, true);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.updateDropdownPosition);
    window.removeEventListener('scroll', this.updateDropdownPosition, true);

    if (this.observer) {
      this.observer.disconnect();
    }
  },
});
</script>

<template>
  <div ref="dropdown" :data-dropdown="dataDropdown" class="custom-dropdown"
       :class="[customClass, disabled || loading ? 'disabled' : '']">
    <div class="custom-dropdown-box d-flex justify-content-between">
      <div class="label">
        <slot name="label" :is-open="isOpen">{{ label }}</slot>
      </div>

      <div v-if="errorMessage" class="error-message">({{ errorMessage }})</div>
    </div>

    <div class="position-relative">
      <div tabindex="0"
           class="selected-option cursor-pointer"
           @click="handleToggleDropdown"
           :class="[
               errorMessage ? 'error' : '',
               errorMessage ? 'error-multiselect-field': ''
           ]"
           ref="dropdownOption">
        <div class="text">
          <template v-if="loading">
            <LoadingIcon :type="LoadingType.SMALL"/>
          </template>
          <template v-else>
            <template v-if="selectedItems.length === 0">
              {{ placeholder }}
            </template>
            <template v-else>
              <slot name="selected-items" :selectedItems="selectedItems">
                {{ selectedItemText }}
              </slot>
            </template>
          </template>
        </div>
        <ArrowControl :is-open="isOpen" class="item-arrow"/>
      </div>
    </div>
    <div class="dropdown-container" ref="dropdownContainer"/>

    <Teleport to="body" :disabled="inlineDropdown">
      <ul v-if="isOpen"
          v-show="isVisible"
          class="list teleported"
          :class="{ 'above': dropdownPosition === 'top' }"
          :style="teleportStyle"
          ref="dropdownList"
          :data-dropdown="dataDropdown"
      >
        <li v-if="$slots['dropdown-header']" class="dropdown-header">
          <slot name="dropdown-header"/>
        </li>
        <li class="search-container" v-if="!disabledSearch">
          <Input
              ref="searchInput"
              type="text"
              v-model:value="searchText"
              placeholder="Search..."
              @keydown.enter="handleEnter"
              class="search-input"
              input-class="dropdown"
              clear-icon
              @clear="searchText = ''"
          />
        </li>
        <li
            v-for="item in filteredItems"
            :key="item.id as PropertyKey"
            class="item"
            :class="{ 'highlight': isSelected(item.id) }"
            @click="selectItem(item.id, $event)"
        >
          <template v-if="multiple && maxSize !== 1">
            <Checkbox
                :model-value="isSelected(item.id)"
                @change="selectItem(item.id)"
            />
          </template>

          <div class="name w-100">
            <slot :item="item">
              {{ item.name }}
            </slot>
          </div>
        </li>

        <li v-if="noDataLoaded && !data.length" class="item disable-hover">
          No data found
        </li>
        <li class="loading-more" v-if="loading">
          <LoadingIcon :type="LoadingType.SMALL"/>
        </li>

        <li ref="sentinel" class="sentinel" v-show="data.length > 0"/>
      </ul>
    </Teleport>
  </div>
</template>

<style lang="scss">
[data-dropdown="default"] {
  --dropdown-bg: rgba(68, 71, 72, 0.4);
  --dropdown-border: rgba(255, 255, 255, 0.2);
  --dropdown-icon: invert(83%) sepia(0%) saturate(1159%) hue-rotate(198deg) brightness(96%) contrast(83%);
  --dropdown-text: rgba(255, 255, 255, 0.75);

  --dropdown-bg-hover: rgba(48, 51, 51, 1);
  --dropdown-border-hover: rgba(134, 164, 179, 1);
  --dropdown-icon-hover: invert(100%) sepia(1%) saturate(930%) hue-rotate(231deg) brightness(111%) contrast(80%);
  --dropdown-text-hover: rgba(184, 232, 255, 1);
}

[data-dropdown="open"] {
  --dropdown-bg: rgba(54, 77, 87, 0.4);
  --dropdown-border: rgba(134, 164, 179, 1);
  --dropdown-icon: invert(80%) sepia(39%) saturate(506%) hue-rotate(178deg) brightness(103%) contrast(107%);
  --dropdown-text: rgba(184, 232, 255, 1);

  --dropdown-bg-hover: rgba(48, 51, 51, 1);
  --dropdown-border-hover: rgba(134, 164, 179, 1);
  --dropdown-icon-hover: invert(100%) sepia(1%) saturate(930%) hue-rotate(231deg) brightness(111%) contrast(80%);
  --dropdown-text-hover: rgba(184, 232, 255, 1);

  z-index: 9999999;
}

.custom-dropdown {
  position: relative;

  &.disabled {
    cursor: not-allowed !important;

    .selected-option {
      opacity: 0.5;
    }
  }

  .label {
    font-family: "Artifex CF Light", serif;
    font-size: 12px;

    color: rgba(255, 255, 255, 0.5);
  }

  .selected-option {
    background: var(--dropdown-bg);
    border: 1px solid var(--dropdown-border);
    border-radius: 2px;

    width: 100%;
    height: 30px;
    padding: 0 10px;

    display: flex;
    align-items: center;

    font-family: "Artifex CF Regular", serif;
    color: var(--dropdown-text);

    &.error {
      border-color: rgba(255, 0, 0, 0.6) !important;
    }

    .text {
      flex: 1 1 auto;
      min-width: 0;

      max-height: 17px;
      overflow: hidden;
      text-overflow: ellipsis;

      font-family: "Artifex CF Regular", sans-serif;
      font-size: 12px;

      color: var(--dropdown-text);
    }

    .item-arrow {
      flex: 0 0 12px;
      width: 8px;
      height: 6px;
      margin-left: 8px;

      filter: var(--dropdown-icon);
    }

    &:hover {
      background: var(--dropdown-bg-hover);
    }
  }

  .dropdown-container {
    position: relative;
  }
}

.list {
  margin: 0;
  padding: 0;
  list-style-type: none;
  max-height: 201px;
  overflow-y: auto;
  z-index: 9999;
  background: rgba(48, 51, 51, 1);

  .dropdown-header {
    padding: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .search-container {
    position: sticky;
    top: 0;
    z-index: 101;
    padding: 0 5px;
    background: rgba(48, 51, 51, 1);
  }

  .item {
    padding: 0 10px;
    height: 30px;
    background: rgba(63, 66, 68, 1);
    cursor: pointer;
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: start;

    flex-direction: row;
    gap: 5px;

    font-family: "Artifex CF Regular", serif;
    font-size: 12px;

    color: rgba(255, 255, 255, 0.9);

    .name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }

    &.disable-hover {
      background-color: rgba(48, 51, 51, 1);

      &:hover {
        background-color: rgba(48, 51, 51, 1);
        color: rgba(255, 255, 255, 0.9);
        cursor: default;
      }
    }

    &:hover {
      background: var(--dropdown-bg-hover);
      color: var(--dropdown-text-hover);
    }

    &.highlight {
      background: #303333;

      color: #B8E8FF;
    }
  }
}

//}
</style>